import { useState, useRef, useEffect, forwardRef, useMemo } from 'react';
import { get } from 'lodash';
import { TaskItem } from './taskItem';
import { CheckboxCell } from './checkboxCell';
import { useDeal } from './context';
import { SortKnob } from './sortKnob';
import { ReactSortable } from 'react-sortablejs';
import {
  ArrowsUpDownIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  InboxArrowDownIcon
} from '@heroicons/react/24/outline';
import { Button, Confirm } from '@components';
import { useLongPress } from '../utils/longpress';
import { COLOR_GROUPS_RAW } from '../utils/colors';

const nestedDivClasses =
  '[&>div:not(.checkbox)]:px-[0.8rem] [&>div]:py-1 [&>div]:truncate [&>div>span]:truncate';

const TaskItemGroup = forwardRef(({ groupId }, ref) => {
  const {
    actions = {},
    refreshing,
    taskGroupById,
    selectedItemIds,
    setSelectedItemIds,
    dealId,
    gridCols,
    toggleGroup,
    closedGroupIds,
    visibleTaskItemIdsForGroup,
    filters,
    setAddModalIsOpened,
    currentTaskItemFilter
  } = useDeal();

  const group = get(taskGroupById, groupId);
  const messagesCount = get(group, 'messages_count', 0);
  const topicId = get(group, 'topic_id');
  const sortedTaskItemIds = visibleTaskItemIdsForGroup(group);

  const [taskItemIds, setTaskItemIds] = useState(sortedTaskItemIds);
  const groupSelectedItems = (selectedItemIds || []).filter(itemId => taskItemIds.includes(itemId));
  const allSelected = groupSelectedItems.length === taskItemIds.length;
  const colors = get(COLOR_GROUPS_RAW, group.color, COLOR_GROUPS_RAW.default);
  const colorClasses = Object.values(colors).join(' ');
  const dividerClass = 'divide-slate-200';
  const sortingTrigger = JSON.stringify(taskItemIds.map((id, position) => ({ id, position })));

  useEffect(() => {
    setTaskItemIds(sortedTaskItemIds);
  }, [
    filters.selectedEditors,
    filters.selectedViewers,
    filters.selectedStatuses,
    filters.selectedDeadline,
    sortedTaskItemIds.length,
    currentTaskItemFilter
  ]);

  const inputRef = useRef();
  const [label, setLabel] = useState(get(group, 'label') || '');
  const [editableLabel, setEditableLabel] = useState(false);
  const inputLongpress = useLongPress(() => {
    setEditableLabel(true);
  });
  useEffect(() => {
    if (!!editableLabel) {
      inputRef.current.focus();
    }
  }, [editableLabel, inputRef.current]);
  const updateGroup = task_group =>
    actions.updateGroup(groupId, { task_group }, 'Le groupe a été mis à jour.');

  // VISIBILITY
  const openedGroup = !closedGroupIds.includes(String(groupId));

  // create task item
  const [newTaskItemLabel, setNewTaskItemLabel] = useState('');

  // rand
  const menuIconClasses = 'w-5 h-5';

  if (sortedTaskItemIds.length <= 0) {
    return null;
  }

  return (
    <div
      data-id={groupId}
      ref={ref}
      className={`group/group relative shadow-lg flex flex-col divide-y rounded-lg  border ${colorClasses}`}
    >
      <div className="group flex items-center relative justify-between px-4 py-2">
        <div className="font-hmedium w-full flex-shrink" {...inputLongpress.handlers}>
          {editableLabel ? (
            <form
              className="w-full relative"
              onSubmit={e => {
                e.preventDefault();
                if (label !== '') {
                  updateGroup({ label });
                  setEditableLabel(false);
                }
              }}
            >
              <input
                className="w-full bg-transparent border-0"
                ref={inputRef}
                type="text"
                onBlur={e => {
                  if (e.target.value !== '') {
                    updateGroup({ label: e.target.value });
                  }
                  setEditableLabel(false);
                }}
                onChange={e => setLabel(e.target.value)}
                value={label}
              />
              <input type="submit" className="absolute opacity-0" />
            </form>
          ) : (
            label
          )}
          {topicId && (
            <a href={`/topics/${topicId}`} data-turbo-frame="dialog" className="relative ml-3">
              {messagesCount > 0 && (
                <span className="absolute rounded-full bg-red-600 text-white w-3 h-3 flex items-center justify-center text-[8px] top-[-3px] right-[-5px]">
                  {messagesCount}
                </span>
              )}
              <InboxArrowDownIcon className={`${menuIconClasses} hidden lg:inline-block`} />
            </a>
          )}
        </div>
        <div className="flex items-center space-x-4">
          <Confirm
            loading={refreshing}
            title="Supprimer le groupe ?"
            subtitle="Les démarches seront supprimées. Cette action est irréversible."
            onConfirm={() => actions.deleteGroup(groupId, 'Le groupe a été supprimé.')}
          >
            <TrashIcon className={`${menuIconClasses} hidden lg:inline-block`} />
          </Confirm>
          <button className="handle">
            <ArrowsUpDownIcon className={menuIconClasses} />
          </button>
          <button onClick={() => toggleGroup(groupId)}>
            {openedGroup ? (
              <ChevronDownIcon className={menuIconClasses} />
            ) : (
              <ChevronUpIcon className={menuIconClasses} />
            )}
          </button>
        </div>
      </div>
      <div
        className={`${openedGroup ? 'block' : 'hidden'} divide-x grid ${gridCols} ${dividerClass} ${nestedDivClasses} [&>div]:text-xs bg-slate-50 font-hmedium [&>div>span]:text-slate-600`}
      >
        <CheckboxCell
          checked={allSelected}
          indeterminate={groupSelectedItems.length >= 1 && !allSelected}
          onClick={() =>
            setSelectedItemIds(prevItemIds => {
              if (prevItemIds.some(itemId => taskItemIds.includes(itemId))) {
                return prevItemIds.filter(itemId => !taskItemIds.includes(itemId));
              } else {
                return [...prevItemIds, ...taskItemIds];
              }
            })
          }
        />
        <div>
          <span>Nom</span>
        </div>
        <div className="hidden lg:inline-flex items-center justify-center">
          <span>Fichiers</span>
        </div>
        <div className="hidden lg:inline-flex items-center justify-center">
          <span>Validité</span>
        </div>
        <div className="inline-flex items-center justify-center">
          <span>Statut</span>
        </div>
        <div className="inline-flex items-center justify-center">
          <span>Échéance</span>
        </div>
        <div className="hidden lg:inline-flex items-center justify-center">
          <span>Affectation</span>
        </div>
        <div className="hidden lg:inline-flex items-center justify-center">
          <span>Partage</span>
        </div>
        <div className="hidden lg:inline-flex items-center justify-center">
          <span>Initiateur</span>
        </div>
      </div>
      <ReactSortable
        className={`flex flex-col divide-y bg-white ${dividerClass} ${openedGroup ? 'flex' : 'hidden'}`}
        handle=".handle"
        list={taskItemIds.map(id => ({
          id
        }))}
        setList={newArray => {
          const newOrderedIds = newArray.map((element, index) => element.id);
          setTaskItemIds(newOrderedIds);

          // backend
          const updatePayload = newOrderedIds.map((id, position) => ({ id, position }));
          if (sortingTrigger !== JSON.stringify(updatePayload)) {
            actions.sortTaskItems(updatePayload, 'Le tri des démarches a été mis à jour.');
          }
        }}
        group={{
          name: 'items',
          pull: true,
          put: true
        }}
        onAdd={evt => {
          const itemId = evt.item.dataset.id;
          if (itemId) {
            actions.updateTaskItem(itemId, { group_id: groupId }, 'La démarche a été déplacée.');
          }
        }}
        animation={200}
        delayOnTouchStart={true}
        delay={2}
      >
        {taskItemIds.map(taskItemId => (
          <TaskItem
            classes={`${nestedDivClasses} ${gridCols}`}
            taskItemId={taskItemId}
            key={taskItemId}
          />
        ))}
      </ReactSortable>
      <div className={`bg-white overflow-hidden rounded-b-lg ${openedGroup ? 'flex' : 'hidden'}`}>
        <form
          className="relative w-full"
          onSubmit={e => {
            if (newTaskItemLabel.length >= 2) {
              e.preventDefault();
              actions.createTaskItem(groupId, newTaskItemLabel, 'La démarche a été créée.');
              setNewTaskItemLabel('');
            }
          }}
        >
          <input
            value={newTaskItemLabel}
            onChange={e => setNewTaskItemLabel(e.target.value)}
            type="text"
            placeholder="Ajouter une démarche..."
            className="text-sm py-2 px-4 w-full pl-4 lg:pl-[73px]"
          />
          <input type="submit" className="absolute opacity-0" />
        </form>
      </div>
    </div>
  );
});

export default TaskItemGroup;
